import React, { Component } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { Button } from 'react-bootstrap';
import FieldPalette, { FIELD_TYPES } from './FieldPalette';
import BuilderCanvas from './BuilderCanvas';
import FieldConfigPanel from './FieldConfigPanel';
import SectionManager from './SectionManager';
import FormPreview from './FormPreview';
import {
    getFormDefinitionById,
    createFormDefinition,
    updateFormDefinition
} from '../../../services/clinicPortalServices/formDefinitionService';
import toastr from 'toastr';

export default class FormBuilderEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            formId: this.props.formId || null,
            name: '',
            slug: '',
            description: '',
            entityType: '',
            isEntityBound: true,
            isGlobal: true,
            modalSize: 'lg',
            submitLabel: 'Save Changes',
            status: 'draft',
            version: 1,
            sections: [],
            fields: [],
            selectedFieldId: null,
            showPreview: false,
            loading: false,
            showSections: false
        };
    }

    componentDidMount() {
        if (this.state.formId) {
            this.loadFormDefinition();
        }
    }

    loadFormDefinition = () => {
        this.setState({ loading: true });
        getFormDefinitionById(this.state.formId).then((res) => {
            if (res.status === 200 && res.data) {
                const fd = res.data;
                this.setState({
                    name: fd.name || '',
                    slug: fd.slug || '',
                    description: fd.description || '',
                    entityType: fd.entityType || '',
                    isEntityBound: fd.isEntityBound !== false,
                    isGlobal: fd.isGlobal !== false,
                    modalSize: fd.modalSize || 'lg',
                    submitLabel: fd.submitLabel || 'Save Changes',
                    status: fd.status || 'draft',
                    version: fd.version || 1,
                    sections: fd.sections || [],
                    fields: (fd.fields || []).sort((a, b) => (a.order || 0) - (b.order || 0)),
                    loading: false
                });
            } else {
                this.setState({ loading: false });
                toastr.error('Form definition not found');
            }
        });
    };

    handleMetaChange = (key, value) => {
        this.setState({ [key]: value });
    };

    handleDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;

        // Dragging from palette to canvas
        if (source.droppableId === 'palette' && destination.droppableId === 'canvas') {
            const fieldType = draggableId.replace('palette-', '');
            this.addFieldFromPalette(fieldType, destination.index);
            return;
        }

        // Reordering within canvas
        if (source.droppableId === 'canvas' && destination.droppableId === 'canvas') {
            const fields = [...this.state.fields];
            const [moved] = fields.splice(source.index, 1);
            fields.splice(destination.index, 0, moved);
            fields.forEach((f, i) => {
                f.order = i + 1;
            });
            this.setState({ fields });
        }
    };

    addFieldFromPalette = (fieldType, index) => {
        const fieldId = fieldType + '_' + Date.now();
        const typeInfo = FIELD_TYPES.find((ft) => ft.type === fieldType);
        const newField = {
            fieldId: fieldId,
            type: fieldType,
            label: typeInfo ? typeInfo.label : fieldType,
            modelPath: '',
            colSpan: fieldType === 'address' || fieldType === 'section' ? 12 : 6,
            order: index + 1,
            sectionId: '',
            validation: {},
            options: ['select', 'multiselect', 'radio'].includes(fieldType) ? [] : undefined,
            dataSource: ['objectIdRef', 'objectIdRefMulti'].includes(fieldType)
                ? { endpoint: '', labelField: 'name', valueField: '_id' }
                : undefined,
            config: fieldType === 'tel' ? { formatPhone: true } : {}
        };

        const fields = [...this.state.fields];
        fields.splice(index, 0, newField);
        fields.forEach((f, i) => {
            f.order = i + 1;
        });
        this.setState({ fields, selectedFieldId: fieldId });
    };

    handleSelectField = (fieldId) => {
        this.setState({ selectedFieldId: fieldId });
    };

    handleRemoveField = (fieldId) => {
        this.setState((prev) => ({
            fields: prev.fields.filter((f) => f.fieldId !== fieldId),
            selectedFieldId: prev.selectedFieldId === fieldId ? null : prev.selectedFieldId
        }));
    };

    handleUpdateField = (fieldId, updatedField) => {
        this.setState((prev) => {
            const newState = {
                fields: prev.fields.map((f) => (f.fieldId === fieldId ? updatedField : f))
            };
            if (prev.selectedFieldId === fieldId && updatedField.fieldId !== fieldId) {
                newState.selectedFieldId = updatedField.fieldId;
            }
            return newState;
        });
    };

    handleUpdateSections = (sections) => {
        this.setState({ sections });
    };

    handleSave = () => {
        const {
            formId,
            name,
            slug,
            description,
            entityType,
            isEntityBound,
            isGlobal,
            modalSize,
            submitLabel,
            status,
            version,
            sections,
            fields
        } = this.state;

        if (!name || !slug) {
            toastr.error('Name and slug are required');
            return;
        }

        const formDef = {
            name,
            slug,
            description,
            entityType,
            isEntityBound,
            isGlobal,
            modalSize,
            submitLabel,
            status,
            version,
            sections,
            fields
        };

        if (formId) {
            formDef._id = formId;
            updateFormDefinition(formDef).then((res) => {
                toastr.success('Form definition saved');
            });
        } else {
            createFormDefinition(formDef).then((res) => {
                if (res.data && res.data._id) {
                    this.setState({ formId: res.data._id });
                }
                toastr.success('Form definition created');
            });
        }
    };

    handleShowPreview = () => {
        this.setState({ showPreview: true });
    };

    getPreviewDefinition = () => {
        return {
            name: this.state.name,
            sections: this.state.sections,
            fields: this.state.fields,
            modalSize: this.state.modalSize,
            submitLabel: this.state.submitLabel
        };
    };

    render() {
        const selectedField = this.state.fields.find((f) => f.fieldId === this.state.selectedFieldId);

        if (this.state.loading) {
            return (
                <div className="text-center p-4">
                    <i className="fa fa-spinner fa-spin fa-2x" />
                    <p className="mt-2">Loading form definition...</p>
                </div>
            );
        }

        return (
            <DragDropContext onDragEnd={this.handleDragEnd}>
                <div style={{ padding: '15px' }}>
                    {/* Top Bar */}
                    <div className="row mb-3">
                        <div className="col-md-8">
                            <h4>
                                {this.state.formId ? 'Edit' : 'Create'} Form Definition
                                {this.state.name && ': ' + this.state.name}
                            </h4>
                        </div>
                        <div className="col-md-4 text-right">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="mr-2"
                                onClick={this.handleShowPreview}
                            >
                                <i className="fa fa-eye" /> Preview
                            </Button>
                            <Button variant="primary" size="sm" onClick={this.handleSave}>
                                <i className="fa fa-save" /> Save
                            </Button>
                            {this.props.onBack && (
                                <Button variant="secondary" size="sm" className="ml-2" onClick={this.props.onBack}>
                                    <i className="fa fa-arrow-left" /> Back
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Meta Fields */}
                    <div
                        className="row mb-3"
                        style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px' }}
                    >
                        <div className="col-md-3">
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Form Name *</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={this.state.name}
                                onChange={(e) => this.handleMetaChange('name', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Slug *</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={this.state.slug}
                                onChange={(e) => this.handleMetaChange('slug', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Entity Type</label>
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                value={this.state.entityType}
                                onChange={(e) => this.handleMetaChange('entityType', e.target.value)}
                                placeholder="e.g. company"
                            />
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Status</label>
                            <select
                                className="form-control form-control-sm"
                                value={this.state.status}
                                onChange={(e) => this.handleMetaChange('status', e.target.value)}
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Modal</label>
                            <select
                                className="form-control form-control-sm"
                                value={this.state.modalSize}
                                onChange={(e) => this.handleMetaChange('modalSize', e.target.value)}
                            >
                                <option value="sm">Small</option>
                                <option value="lg">Large</option>
                                <option value="xl">XL</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Sections</label>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="d-block"
                                onClick={() => this.setState({ showSections: !this.state.showSections })}
                            >
                                <i className="fa fa-list" /> Manage ({this.state.sections.length})
                            </Button>
                        </div>
                    </div>

                    {/* Section Manager */}
                    {this.state.showSections && (
                        <div className="mb-3" style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
                            <SectionManager
                                sections={this.state.sections}
                                onUpdateSections={this.handleUpdateSections}
                            />
                        </div>
                    )}

                    {/* 3-Panel Layout */}
                    <div className="row">
                        {/* Left: Field Palette */}
                        <div
                            className="col-md-2"
                            style={{
                                borderRight: '1px solid #ddd',
                                maxHeight: 'calc(100vh - 400px)',
                                overflowY: 'auto'
                            }}
                        >
                            <FieldPalette />
                        </div>

                        {/* Center: Canvas */}
                        <div className="col-md-6" style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                            <BuilderCanvas
                                fields={this.state.fields}
                                selectedFieldId={this.state.selectedFieldId}
                                onSelectField={this.handleSelectField}
                                onRemoveField={this.handleRemoveField}
                            />
                        </div>

                        {/* Right: Config Panel */}
                        <div
                            className="col-md-4"
                            style={{
                                borderLeft: '1px solid #ddd',
                                maxHeight: 'calc(100vh - 400px)',
                                overflowY: 'auto'
                            }}
                        >
                            <FieldConfigPanel
                                field={selectedField}
                                sections={this.state.sections}
                                onUpdateField={this.handleUpdateField}
                            />
                        </div>
                    </div>

                    {/* Preview Modal */}
                    <FormPreview
                        show={this.state.showPreview}
                        formDefinition={this.getPreviewDefinition()}
                        onClose={() => this.setState({ showPreview: false })}
                    />
                </div>
            </DragDropContext>
        );
    }
}
